import React, { 
        useEffect,
        useRef, 
        useState                    } from 'react';
import { useTheme                   } from 'styled-components';

import { ArrowBackVector            } from 'src/components/vector/arrow-back';
import { MagicStickVector           } from 'src/components/vector/magic-stick';
import { TypeOfProductsSheetsVector } from 'src/components/vector/type-of-products/type-of-products-sheets';

import { Button                     } from 'src/components/common/button';
import { ButtonWithIcon             } from 'src/components/common/button-with-icon';
import { Typography                 } from 'src/components/common/typography';
import { Loader                     } from 'src/components/common/loader';
import { OneActionDialog            } from 'src/components/common/one-action-dialog';
import { Dialog                     } from 'src/components/common/dialog';

import { NavBar                     } from 'src/components/features/common/nav-bar';
import { PhotoView                  } from 'src/components/features/main/photo-view';
import { ProductCountResultDialog   } from 'src/components/features/main/product-count-result-dialog';

import { CameraForCountLayout,
         ControllersContainer,
         CountInfoContainer,
         IconInfoContainer,
         LabelInfoContainer,
         ResultContainer,
         Screen,
         ScreenBody,
         ViewerContainer,
         layout_dark,
         layout_light,              } from 'src/containers/main/camera-for-count/layout';

import { Urls                       } from 'src/providers/routing';

import Api                            from 'src/services/api';
import { ProductCounterStatusEnum   } from 'src/services/api/types/product-counter';

import { storeApi, 
         useAppDispatch, 
         useAppSelector             } from 'src/store';

import { AsyncOpStatus, Nullable    } from 'src/common';



export const useTaskPolling = (ticket: string) => {
  const dispatch = useAppDispatch();
  const statusPolling = useAppSelector(
    storeApi.main.mainProductCounter.selectors.selectStatusPolling
  );
  const resultsCounterStatus = useAppSelector(
    storeApi.main.mainProductCounter.selectors.selectResultsCounterStatus
  );

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!ticket || ((resultsCounterStatus?.status === ProductCounterStatusEnum.COMPLETED) || statusPolling))
    {
      return;
    }

    dispatch(storeApi.main.mainProductCounter.action.startPolling());

    const poll = async () => {
      const result = await dispatch(
        storeApi.main.mainProductCounter.async.getCounterStatusAsync({ ticket })
      );

      if (
        storeApi.main.mainProductCounter.async.getCounterStatusAsync.fulfilled.match(
          result
        )
      )
      {
        const status = result.payload.status;
        if (status !== ProductCounterStatusEnum.PROCESSING)
        {
          dispatch(storeApi.main.mainProductCounter.action.stopPolling());

          if (status === ProductCounterStatusEnum.ERROR)
          {
            dispatch(storeApi.main.mainProductCounter.action.changeIsOpenErrorDialog())
          }

          if (intervalRef.current)
          {
            clearInterval(intervalRef.current)
          }
        }
      }
    };

    poll();

    intervalRef.current = setInterval(() => poll(), 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      dispatch(storeApi.main.mainProductCounter.action.stopPolling());
    };
  }, [ticket, dispatch]);
};

const getImgResult = async (ticket: string, setUrl: (arg: string) => void) => {
  try {
    if (ticket)
    {
      const resultsImg = await Api.getCounterResult({
        ticket,
      });

      const url = URL.createObjectURL(resultsImg);
      setUrl(url);

      return;
    }
  } catch (error) {
    console.error('Get img result error', error);
  }
};

type ProductCounterScreenElement = {
  layout: CameraForCountLayout;
};

const Navigation: React.FC<ProductCounterScreenElement> = ({ layout }) => {
  return (
    <React.Fragment>
      <NavBar
        variant = { layout.navbarVariant }
        label='Подсчитать'
        startButtons = {[
          {
            id: 'back',
            vector: <ArrowBackVector />,
            action: () => Urls.MainProductCounter.build().navigate(),
          },
        ]}
      />
    </React.Fragment>
  );
};

export const CountInfo: React.FC<{ count: number, layout: CameraForCountLayout }> = ({ count = 0, layout }) => {
  return (
    <CountInfoContainer layout = { layout }>
      <IconInfoContainer layout = { layout }>
        <TypeOfProductsSheetsVector />
      </IconInfoContainer>
      <LabelInfoContainer layout = { layout }>
        <Typography variant = 'steelw400s12lh16'>Количество листов</Typography>
        <Typography variant = 'whitew600s28lh24'>{ count }</Typography>
      </LabelInfoContainer>
    </CountInfoContainer>
  );
};

export const CameraForCount = () => {
  const theme = useTheme();

  const isOpened = useAppSelector(
    storeApi.main.mainProductCountResultDialog.selectors.selectIsOpened
  );

  const opStatus = useAppSelector(
    storeApi.main.mainProductCounter.selectors.selectOpStatus
  );

  const isOpenErrorDialog = useAppSelector(
    storeApi.main.mainProductCounter.selectors.selectIsOpenErrorDialog
  );

  const photo = useAppSelector(
    storeApi.main.mainProductCounter.selectors.selectPhoto
  );

  const resultUploadImage = useAppSelector(
    storeApi.main.mainProductCounter.selectors.selectResultUploadImage
  );

  const resultsCounterStatus = useAppSelector(
    storeApi.main.mainProductCounter.selectors.selectResultsCounterStatus
  );

  const dispatch = useAppDispatch();

  useTaskPolling(resultUploadImage?.ticket || '');

  const [imageSrc, setImageSrc] = useState<Nullable<string>>(null);

  const layout = theme.colorScheme === 'light' ? layout_light : layout_dark;

  useEffect(() => {
    if (photo)
    {
      setImageSrc(URL.createObjectURL(photo));
    }
  }, [photo])

  useEffect(() => {
    if (
      resultsCounterStatus &&
      resultsCounterStatus.status === ProductCounterStatusEnum.COMPLETED
    )
    {
      getImgResult(resultsCounterStatus?.ticket, setImageSrc);
    }
  }, [resultsCounterStatus]);

  const handleNewCountTap = () => {
    dispatch(storeApi.main.mainProductCountResultDialog.action.setIsOpened());
    setImageSrc(null);
  };

  const handleChangeAnotherPhotoTap = () => {
    Urls.MainProductCounter.build().navigate();
  };

  const handleUploadForCount = async (file: File | null) => {
    try {
      if (!file)
      {
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      dispatch(
        storeApi.main.mainProductCounter.async.uploadImageForCounterAsync(
          formData
        )
      );
    } catch (err) {
      console.error('Upload error', err);
    }
  }

  const handleErrorDialogClick = () => {
    Urls.MainProductCounter.build().navigate();
    dispatch(storeApi.main.mainProductCounter.action.changeIsOpenErrorDialog())
  }

  return (
    <Screen layout = { layout }>
      <Loader isLoading = { opStatus === AsyncOpStatus.BUSY } variant = 'loader_main' />

      <Navigation layout = { layout } />
      <ScreenBody layout = { layout }>
        <ViewerContainer layout = { layout }>
          <PhotoView
            imgUrl = { imageSrc }
            variant = 'photo_view_main'
          />
        </ViewerContainer>
        {opStatus === AsyncOpStatus.SUCCESS ? (
          <ResultContainer layout = { layout }>
            <CountInfo count={resultsCounterStatus?.number_of_sheets || 0} layout = { layout }/>
            <ButtonWithIcon
              variant = 'h48outlineround'
              labelPosition = 'center'
              label = 'Новый подсчет'
              onTap = { handleNewCountTap }
            />
          </ResultContainer>
        ) : (
          <ControllersContainer layout = { layout }>
            <ButtonWithIcon
              variant = 'h48outlineround'
              labelPosition = 'center'
              leftSideIcon = { <MagicStickVector /> }
              label = 'Подсчитать'
              onTap = { () => handleUploadForCount(photo) }
            />
            <Button
              variant = 'h48empty'
              label = 'Выбрать другое фото'
              onTap = { handleChangeAnotherPhotoTap }
            />
          </ControllersContainer>
        )}
      </ScreenBody>
      <ProductCountResultDialog isOpened = { isOpened } variant = 'result_dialog_main' />
      <Dialog variant = { 'light' } isOpened = { isOpenErrorDialog }>
        <OneActionDialog
          variant = { 'light' }
          caption = 'Произошла ошибка'
          actionLabel = 'Попробовать еще раз'
          message = { resultsCounterStatus?.error_detail || 'Неизвестная ошибка' }
          onAction = { handleErrorDialogClick }
        />
      </Dialog>
    </Screen>
  );
};
